import { Controller, useFormContext } from 'react-hook-form'

import { SolverIcon } from '@/shared/components/Icon/SolverIcon'
import { TextField } from '@/shared/components/TextField/TextField'
import { TimePicker } from '@/shared/components/TimePicker/TimePicker'
import { pxToRem } from '@/shared/styles/themes'
import { Checkbox, FormControlLabel, Grid2 as Grid, Typography } from '@mui/material'

import { BulkEditCheckbox } from '../BulkEditCheckbox'

type CostsTabProps = {
  isReadOnly: boolean
  isBulkEdit: boolean
}

export const CostsTab: React.FC<CostsTabProps> = ({
  isReadOnly,
  isBulkEdit,
}) => {
  const { control } = useFormContext()

  return (
    <Grid container spacing={1}>
      <Grid size={{ xs: 12 }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mt: 1,
            mb: 1,
          }}
        >
          Costs
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="costFixed"
          control={control}
          rules={{
            required: 'Fixed cost is required',
            min: {
              value: 0,
              message: 'Fixed cost must be greater than or equal to 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label="Fixed Cost ($)"
                placeholder="Enter fixed cost"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="costFixed" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="hiddenCostFixed"
          control={control}
          rules={{
            min: {
              value: 0,
              message: 'Hidden fixed cost must be greater than or equal to 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Hidden Fixed Cost ($)</>}
                placeholder="Enter hidden fixed cost"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="hiddenCostFixed" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="deliverySegmentFixedPenalty"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Delivery Segment Fixed Penalty ($)</>}
                placeholder="Enter delivery segment fixed penalty"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="deliverySegmentFixedPenalty" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="deliverySegmentDistancePenalty"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Delivery Segment Distance Penalty ($)</>}
                placeholder="Enter delivery segment distance penalty"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="deliverySegmentDistancePenalty" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="loadedVolumeDistancePenalty"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Loaded Volume Distance Penalty ($)</>}
                placeholder="Enter loaded volume distance penalty"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="loadedVolumeDistancePenalty" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="costPerHour"
          control={control}
          rules={{
            required: 'Cost per hour is required',
            min: {
              value: 0,
              message: 'Cost per hour must be greater than or equal to 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label="Per Hour Cost ($)"
                placeholder="Enter cost per hour"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="costPerHour" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="hiddenCostPerHour"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Hidden Per Hour Cost ($)</>}
                placeholder="Enter hidden cost per hour"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="hiddenCostPerHour" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="costPerKm"
          control={control}
          rules={{
            required: 'Cost per km is required',
            min: {
              value: 0,
              message: 'Cost per km must be greater than or equal to 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label="Per Km Cost ($)"
                placeholder="Enter cost per km"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="costPerKm" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="hiddenCostPerKm"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Hidden Per Km Cost ($)</>}
                placeholder="Enter hidden cost per km"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="hiddenCostPerKm" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="overtimeCostPerHour1"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Overtime Cost Per Hour 1 ($)</>}
                placeholder="Enter overtime cost per hour 1"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="overtimeCostPerHour1" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="overtimeKickIn1"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TimePicker
                {...field}
                label={<><SolverIcon />Overtime Kick In 1</>}
                disabled={isReadOnly}
                onAccept={field.onChange}
                format="HH:mm"
                views={['hours', 'minutes']}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'HH:mm',
                    error: !!fieldState?.error,
                    helperText: fieldState?.error?.message,
                  },
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="overtimeKickIn1" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="overtimeCostPerHour2"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Overtime Cost Per Hour 2 ($)</>}
                placeholder="Enter overtime cost per hour 2"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="overtimeCostPerHour2" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="overtimeKickIn2"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TimePicker
                {...field}
                label={<><SolverIcon />Overtime Kick In 2</>}
                disabled={isReadOnly}
                onAccept={field.onChange}
                format="HH:mm"
                views={['hours', 'minutes']}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'HH:mm',
                    error: !!fieldState?.error,
                    helperText: fieldState?.error?.message,
                  },
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="overtimeKickIn2" />}
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="isPaidBreaks"
          control={control}
          render={({ field }) => (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    disabled={isReadOnly}
                  />
                }
                label="Paid Breaks?"
              />
              {isBulkEdit && <BulkEditCheckbox name="isPaidBreaks" />}
            </>
          )}
        />
      </Grid>
    </Grid>
  )
}
